import React, { useRef, useEffect, useState } from 'react';
import FallbackImage from './FallbackImage';
import Header from './Header';
import FullPageScroll from './FullPageScroll';
import { CALENDLY_URL, SITE_URL } from '../config';
import styles from './LandingPage.module.css';

function FadeInCard({ children, delay = 0 }) {
  const domRef = useRef();
  const [isVisible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      entries => { entries.forEach(e => { if (e.isIntersecting) setVisible(true); }); },
      { threshold: 0.1 }
    );
    if (domRef.current) observer.observe(domRef.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      className={`fade-in-card${isVisible ? ' is-visible' : ''}`}
      ref={domRef}
      style={{ '--stagger-delay': `${delay}ms` }}
    >
      {children}
    </div>
  );
}

function SectionHeading({ id, children }) {
  return (
    <div id={id} className={styles.sectionHeadingWrap}>
      <span className={styles.sectionHeadingBadge} aria-hidden="true" />
      <h3 className={styles.sectionHeading}>{children}</h3>
      <div className={styles.sectionHeadingLine} aria-hidden="true" />
    </div>
  );
}

const serviceIcons = [
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M4 20c0-4.418 3.582-8 8-8s8 3.582 8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 21l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <circle cx="12" cy="6.5" r="2.5" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="6.5" cy="9.5" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <circle cx="17.5" cy="9.5" r="2" stroke="currentColor" strokeWidth="1.5"/>
    <path d="M9.5 20c0-2.485 1.119-4.5 2.5-4.5s2.5 2.015 2.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M3 18c0-1.657 1.119-3 2.5-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M21 18c0-1.657-1.119-3-2.5-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 22V14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
    <path d="M12 17c0 0-5-2-5-7 3.5 0 5 3 5 7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
    <path d="M12 13c0 0 5-2 5-7-3.5 0-5 3-5 7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 12c-1.5-2-3.5-3.5-5.5-3.5C3.739 8.5 1.5 10.739 1.5 13.5S3.739 18.5 6.5 18.5c2 0 4-1.5 5.5-3.5 1.5 2 3.5 3.5 5.5 3.5C20.261 18.5 22.5 16.261 22.5 13.5S20.261 8.5 17.5 8.5c-2 0-4 1.5-5.5 3.5z" stroke="currentColor" strokeWidth="1.5"/>
  </svg>,
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <path d="M12 2l2.5 5.5 6 .5-4.5 4 1.5 6-5.5-3-5.5 3 1.5-6L3.5 8l6-.5L12 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"/>
  </svg>,
];

const therapies = [
  { title: 'Individual Therapy', desc: 'Personal growth and healing tailored to you.' },
  { title: 'Couple Therapy', desc: 'Strengthen your relationship and rebuild intimacy.' },
  { title: 'Family Therapy', desc: 'Improve communication and resolve conflicts.' },
  { title: 'Adolescent Therapy', desc: 'Support for teens facing emotional and social challenges.' },
  { title: 'Relationship Therapy', desc: 'Guidance for navigating relationship challenges.' },
  { title: 'Child Therapy', desc: "Support your child's emotional and mental well-being." },
];

const issues = [
  'Anxiety', 'Depression', 'Relationship Issues', 'Grief and Loss', 'Self-Esteem',
  'Parenting', 'Career Challenges', 'Infidelity', 'Family Issues', 'Teen Challenges',
];

const testimonials = [
  { text: 'Mitra helped me slow down, make sense of my anxiety, and feel in control again. I do recommend her', author: 'A., Toronto' },
  { text: 'I finally feel understood. Our sessions are gentle but very practical. I leave with expected clarity.', author: 'S., North York' },
  { text: 'We communicate better and fight less. Therapy with Mitra truly changed our relationship.', author: 'J. & R., Couple' },
];

const expertise = [
  {
    title: 'My Approach',
    text: 'I offer collaborative, trauma-informed, and culturally sensitive psychotherapy grounded in a client-centered and strengths-based framework. My work integrates CBT, DBT, IFS, ACT, mindfulness-based practices, and Art Therapy to support emotional awareness, regulation, and meaningful growth within a compassionate and safe therapeutic space.',
  },
  {
    title: 'My Experience',
    text: 'With over six years of experience in mental health and rehabilitation settings, I have supported individuals living with acquired brain injuries, as well as refugees and newcomers navigating complex life transitions. I work with adults, youth, and families facing anxiety, depression, trauma, grief, identity concerns, and relationship challenges.',
  },
  {
    title: 'My Belief',
    text: 'I believe healing begins with awareness, the light that reveals what has always lived within us. What we search for in the world is often already present inside, waiting to be seen with compassion. Through mindful reflection and gentle understanding, we rediscover the strength, wisdom, and resilience that were never truly lost.',
  },
];

const faqs = [
  { q: 'What does a first session look like?', a: "We talk about what brings you in, what you'd like to change, and what's worked or not worked before. You set the pace—no pressure, no judgment." },
  { q: 'How many sessions will I need?', a: 'It varies by person and goals. Some benefit from short‑term focused work (6–8 sessions), others prefer ongoing support.' },
  { q: 'Do you offer a free consultation?', a: "Yes. A free 15‑minute phone or video consultation helps us see if we're a good fit before booking." },
  { q: 'Are sessions covered by insurance?', a: 'Many extended health plans cover Qualifying Registered psychotherapists. Please check with your provider for your specific coverage.' },
  { q: 'Do you offer in‑person or virtual sessions?', a: 'Currently virtual by secure video. Limited in‑person availability may be offered seasonally.' },
];

const SECTION_THEMES = ['light', 'light', 'light', 'light', 'light', 'light', 'light', 'light'];
const SECTION_LABELS = ['Home', 'About', 'Expertise', 'Services', 'Issues', 'Testimonials', 'FAQ', 'Contact'];

const LandingPage = () => {
  useEffect(() => {
    document.title = 'Psychotherapist Toronto & North York | Mitra Behboudi';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Compassionate psychotherapy in Toronto and North York. Mitra Behboudi offers therapy for anxiety, couples counseling, and life transitions. Book a free consultation today.');
    let link = document.querySelector("link[rel='canonical']");
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', `${SITE_URL}/`);
  }, []);

  const bookNow = (e) => {
    if (window.Calendly) {
      e.preventDefault();
      window.Calendly.initPopupWidget({ url: CALENDLY_URL });
    }
  };

  return (
    <div>
      <Header />
      <FullPageScroll sectionThemes={SECTION_THEMES} sectionLabels={SECTION_LABELS}>

        {/* 1 — Hero */}
        <div className={styles.fpPageHero}>
          <section
            className={styles.hero}
            style={{ backgroundImage: 'url(/hero.jpg)', backgroundPosition: 'center', backgroundSize: 'cover', backgroundRepeat: 'no-repeat' }}
          >
            <div className={styles.heroDecorations} aria-hidden="true">
              <div className={`${styles.heroDot} ${styles.heroDot1}`} />
              <div className={`${styles.heroDot} ${styles.heroDot2}`} />
              <div className={`${styles.heroDot} ${styles.heroDot3}`} />
              <div className={`${styles.heroDot} ${styles.heroDot4}`} />
            </div>
            <div className={styles.overlay}>
              <h1 className={styles.heroTitle}>Psychotherapy & Art Therapy for Anxiety, Trauma, and Life Transitions</h1>
              <p className={styles.heroSubtitle}>In the Light of Awareness and Compassion, the Self Begins to Heal</p>
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                onClick={bookNow}
                className={`btn btn-primary btn-lg ${styles.heroBtn}`}
              >
                Book Free Consultation
              </a>
            </div>
          </section>
        </div>

        {/* 2 — About */}
        <div className={styles.fpPageAbout}>
          <SectionHeading id="about">About Me</SectionHeading>
          <section className={`container ${styles.section}`}>
            {/* Mobile-only image above text */}
            <div className="d-block d-md-none text-center">
              <FallbackImage src="/IMG3063.png" alt="Mitra Behboudi" className={styles.aboutImageMobile} />
            </div>
            <div className="row align-items-center">
              <div className="col-md-6 mb-0">
                <p className={styles.aboutParagraph}>I am a Registered Psychotherapist (Qualifying) with the College of Registered Psychotherapists of Ontario (CRPO) and an Art Therapist trained through the Canadian Art Therapy Association (CATA). I provide culturally sensitive, trauma-informed psychotherapy to adults, youth, and families navigating anxiety, depression, trauma, identity concerns, and life transitions.</p>
                <p className={styles.aboutParagraph}>My approach is collaborative, compassionate, and client-centered. I strive to create a safe, respectful, and non-judgmental space where you can explore your experiences, process emotions, and move toward meaningful growth and healing.</p>
                <p className={styles.aboutParagraph}>When appropriate, I integrate creative and expressive processes to support clients in accessing emotions and insights that may be difficult to express through words alone.</p>
                <p className={styles.aboutParagraph}>I offer therapy in English and Persian and am committed to culturally attuned care that honours each individual's story and lived experience.</p>
              </div>
              <div className="col-md-6 d-none d-md-flex align-items-center justify-content-center">
                <FallbackImage src="/IMG3063.png" alt="Mitra Behboudi" className={styles.aboutImageCompact} />
              </div>
            </div>
          </section>
        </div>

        {/* 3 — Expertise */}
        <div className={styles.fpPage}>
          <SectionHeading id="expertise">Expertise</SectionHeading>
          <section className={`container ${styles.section}`}>
            <div className={styles.expertiseGrid}>
              {expertise.map((t, i) => (
                <FadeInCard key={i} delay={i * 120}>
                  <div className={styles.expertiseCard}>
                    <div className={styles.expertiseCardNum} aria-hidden="true">{i + 1}</div>
                    <h5 className={styles.expertiseCardTitle}>{t.title}</h5>
                    <p style={{ color: 'var(--color-text-main)', margin: 0, lineHeight: 1.72, textAlign: 'justify' }}>{t.text}</p>
                  </div>
                </FadeInCard>
              ))}
            </div>
          </section>
        </div>

        {/* 4 — Services */}
        <div className={styles.fpPageServices}>
          <SectionHeading id="services">Services</SectionHeading>
          <section className={`container ${styles.section}`}>
            <div className={styles.servicesGrid}>
              {therapies.map((t, i) => (
                <FadeInCard key={i} delay={i * 80}>
                  <div className={styles.serviceCard} style={{ backgroundImage: 'url(/tilebg.JPG)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                    <div className={styles.serviceIconWrap} aria-hidden="true">
                      {serviceIcons[i]}
                    </div>
                    <h5 style={{ color: 'var(--color-primary-strong)', fontWeight: 700, marginBottom: '0.2rem', fontSize: '0.9rem' }}>{t.title}</h5>
                    <p style={{ color: '#5a5a6a', margin: 0, fontSize: '0.82rem', lineHeight: 1.5 }}>{t.desc}</p>
                  </div>
                </FadeInCard>
              ))}
            </div>
          </section>
        </div>

        {/* 5 — Issues */}
        <div className={styles.fpPageIssues}>
          <SectionHeading id="issues">Issues We Help With</SectionHeading>
          <section className={`container ${styles.section}`}>
            <div className={styles.issuesGrid}>
              {issues.map((issue, i) => (
                <FadeInCard key={i} delay={i * 55}>
                  <div className={styles.issueTile}>{issue}</div>
                </FadeInCard>
              ))}
            </div>
          </section>
        </div>

        {/* 6 — Testimonials */}
        <div className={styles.fpPage}>
          <SectionHeading id="testimonials">Testimonials</SectionHeading>
          <section className={`container ${styles.section}`}>
            <div className={styles.testimonialsGrid}>
              {testimonials.map((t, i) => (
                <FadeInCard key={i} delay={i * 140}>
                  <div className={styles.testimonialCard} style={{ backgroundImage: 'url(/tilebg.JPG)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
                    <blockquote className="blockquote mb-0">
                      <p style={{ color: 'var(--color-text-main)', position: 'relative', zIndex: 1, lineHeight: 1.75 }}>&ldquo;{t.text}&rdquo;</p>
                      <footer className="blockquote-footer" style={{ position: 'relative', zIndex: 1 }}>{t.author}</footer>
                    </blockquote>
                  </div>
                </FadeInCard>
              ))}
            </div>
          </section>
        </div>

        {/* 7 — FAQ */}
        <div className={styles.fpPage}>
          <SectionHeading id="faq">Frequently Asked Questions</SectionHeading>
          <section className={`container ${styles.section}`}>
            <div className={`accordion ${styles.faqAccordion}`} id="faqAccordion">
              {faqs.map((faq, i) => (
                <div className="accordion-item" key={i}>
                  <h2 className="accordion-header" id={`heading${i}`}>
                    <button
                      className={`accordion-button${i !== 0 ? ' collapsed' : ''}`}
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target={`#collapse${i}`}
                      aria-expanded={i === 0 ? 'true' : 'false'}
                      aria-controls={`collapse${i}`}
                    >
                      {faq.q}
                    </button>
                  </h2>
                  <div
                    id={`collapse${i}`}
                    className={`accordion-collapse collapse${i === 0 ? ' show' : ''}`}
                    aria-labelledby={`heading${i}`}
                    data-bs-parent="#faqAccordion"
                  >
                    <div className="accordion-body">{faq.a}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* 8 — Contact + Footer */}
        <div className={styles.fpPage}>
          <SectionHeading id="contact">Contact</SectionHeading>
          <section className={`container ${styles.contactSection}`}>
            <div className="row align-items-center">
              <div className="col-md-6 mb-4 mb-md-0">
                <h2 style={{ color: 'var(--color-primary-strong)', fontWeight: 700, marginBottom: '0.9rem' }}>Ready to start your journey?</h2>
                <p style={{ color: 'var(--color-text-muted)', marginBottom: '1.5rem', lineHeight: 1.7 }}>Book a free consultation or reach out with your questions. We're here to help you find balance and strength.</p>
                <ul className="list-unstyled" style={{ marginBottom: '1.6rem' }}>
                  <li style={{ marginBottom: '0.5rem' }}><strong>Email:</strong> <a href="mailto:mitratherapy@gmail.com" style={{ color: 'var(--color-accent)' }}>mitratherapy@gmail.com</a></li>
                  <li><strong>Phone:</strong> <a href="tel:+16478647160" style={{ color: 'var(--color-accent)' }}>+1 647-864-7160</a></li>
                </ul>
                <a href={CALENDLY_URL} target="_blank" rel="noreferrer" onClick={bookNow} className="btn btn-primary">
                  Book Now
                </a>
              </div>
              <div className="col-md-6 text-center">
                <FallbackImage src="/mb.png" alt="Contact" style={{ maxWidth: 180 }} />
              </div>
            </div>
          </section>
          <footer className={styles.footer}>
            <FallbackImage src="/mb.png" alt="Logo" style={{ width: 46, marginBottom: 10 }} />
            <div style={{ fontWeight: 600, fontSize: '1rem', marginBottom: '0.2rem' }}>Mitra Behboudi Psychotherapy</div>
            <div style={{ fontSize: '0.82rem', color: 'rgba(229, 231, 235, 0.65)', marginBottom: '0.25rem' }}>© 2024 All rights reserved.</div>
            <div className={styles.socialLinks}>
              <a className={styles.socialLink} href="https://www.linkedin.com/in/mitrabehboudi/" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" title="LinkedIn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.45 20.45h-3.56v-5.57c0-1.33-.03-3.04-1.85-3.04-1.85 0-2.13 1.45-2.13 2.94v5.67H9.35V9h3.42v1.56h.05c.48-.9 1.64-1.85 3.38-1.85 3.62 0 4.29 2.38 4.29 5.48v6.26zM5.34 7.43a2.07 2.07 0 1 1 0-4.14 2.07 2.07 0 0 1 0 4.14zM7.12 20.45H3.56V9h3.56v11.45z"/>
                </svg>
              </a>
              <a className={styles.socialLink} href="https://www.instagram.com/mitra_bhb?igsh=bnl3eHFzanozd2Jh" target="_blank" rel="noopener noreferrer" aria-label="Instagram" title="Instagram">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M7 2C4.24 2 2 4.24 2 7v10c0 2.76 2.24 5 5 5h10c2.76 0 5-2.24 5-5V7c0-2.76-2.24-5-5-5H7zm0 2h10c1.66 0 3 1.34 3 3v10c0 1.66-1.34 3-3 3H7c-1.66 0-3-1.34-3-3V7c0-1.66 1.34-3 3-3zm11.25 1.5a1.25 1.25 0 1 0 0 2.5 1.25 1.25 0 0 0 0-2.5zM12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"/>
                </svg>
              </a>
            </div>
            <div style={{ fontSize: '0.76rem', color: 'rgba(229, 231, 235, 0.45)' }}>Privacy Policy | Terms of Use</div>
          </footer>
        </div>

      </FullPageScroll>
    </div>
  );
};

export default LandingPage;
